import { FC, useMemo } from "react";

import { IBandInvitationProps } from "Bands/types";
import { IAPIState, IFormButtons, IAPIError } from "shared/types";
import { FormBuilder } from "shared/components";
import { getFields } from "./formData";
import { useBandContext } from "Bands/contexts";
import { API_ENDPOINTS } from "shared/constants";
import { BAND_PATHS } from "Bands/router";
import { useBandRoute } from "Bands/utils";

interface MemberFormProps {
  apiStatus?: IAPIState;
  defaultValues?: Record<string, any>;
  buttons?: IFormButtons;
  handleSubmit: (stage: IBandInvitationProps) => void;
  error?: boolean | string;
  apiError?: IAPIError | null;
  canManageMemberCategories?: boolean;
  readOnlyUserName?: boolean;
  readOnlyEmail?: boolean;
}

export const MemberForm: FC<MemberFormProps> = ({
  apiStatus,
  error,
  handleSubmit,
  defaultValues,
  canManageMemberCategories = false,
  readOnlyUserName,
  readOnlyEmail,
  ...rest
}) => {
  const { pushRoute } = useBandRoute();
  const { bandId, memberCategories } = useBandContext();

  const fields = useMemo(
    () =>
      getFields({
        memberCategories,
        manageMemberCategories: () =>
          pushRoute(BAND_PATHS.EDIT_BAND_SETTINGS_MEMBER),
        readOnlyEmail,
        canManageMemberCategories,
        readOnlyUserName,
      }),

    [
      pushRoute,
      memberCategories,
      readOnlyUserName,
      readOnlyEmail,
      canManageMemberCategories,
    ]
  );

  const onSubmit = (
    data: Omit<IBandInvitationProps, "band" | "signupUrl" | "joinUrl">
  ) => {
    const band = API_ENDPOINTS.BAND(bandId || "");

    handleSubmit({
      ...data,
      band,
      signupUrl: `${window.location.origin}${BAND_PATHS.ACCEPT_INVITATION}?bandInvite=[INVITE]`,
      joinUrl: `${window.location.origin}${BAND_PATHS.ACCEPT_INVITATION}?bandInvite=[INVITE]`,
    });
  };

  return (
    <FormBuilder
      submitMode="isDirty"
      fields={fields}
      isSubmitting={apiStatus === IAPIState.pending}
      apiErrorMessage={error}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      style={{
        gridTemplateColumns: "1fr 1fr",
        mobile: {
          gridTemplateColumns: "1fr",
        },
      }}
      {...rest}
    />
  );
};
